import React, { ChangeEvent, useEffect, useState } from "react";
import { t } from "i18next";
import {
  Headings,
  SelectDropdown,
  PrimaryButton,
  Table,
} from "@sentara-health/optimahealth_web";
import { Link } from "react-router-dom";
import {
  FeatureFlagKey,
  useAuth,
  useFeatureFlag,
  useGlobalState,
} from "providers";
import {
  EmployerGroupInformationRequest,
  useEmployerGroupInformation,
  useBenefitSummaryUSBC,
  useEmployerBenefitSummaryEOC,
  EmployerBenefitSummaryEOCRequest,
  EmployerInsuranceGroupFileType,
  useEmployerBenefitSummarySOB,
  BenefitSummaryUSBCResponseItem,
} from "@sentara-health/sentara-api-hooks-core";
import {
  formatISODateToMDY,
  formatISODateToISO,
} from "utils/helpers/DateFormate";
import { useGroupInformation } from "./hooks/useGroupInformation";
import { ModalOverlayWithOutClose } from "organisms/popup/ModalOverlayWithOutClose";
import { customSort } from "utils/helpers/HelperFn";

const GroupInformation = () => {
  const { user } = useAuth();
  const { getEmployerGroupInformation } = useEmployerGroupInformation();
  const { getBenefitSummaryUSBC } = useBenefitSummaryUSBC();
  const { getEmployerBenefitSummarySOB } = useEmployerBenefitSummarySOB();

  const isAccountNumberFieldFlagEnabled: boolean = useFeatureFlag(
    FeatureFlagKey.AccountNumberField
  );

  // TODO: Replace with actual account numbers from the API or user object data when ready...
  // Ensure deterministic defaults: sort groups by name then by id
  const sortGroupsByNameThenId = (
    groups: Array<{ groupID: string; groupName: string }>
  ) =>
    (groups || []).slice().sort((a, b) => {
      const byName = customSort(a, b, "groupName");
      if (byName !== 0) return byName;
      return customSort(a, b, "groupID");
    });

  const initialSortedGroups = sortGroupsByNameThenId(user?.groups || []);
  const [dropdownValues, setDropdownValues] = useState({
    userGroupId: initialSortedGroups?.[0]?.groupID ?? "",
    accountNumber: "",
  });
  const [groupInformation, setGroupInformation] = useState<any>(null);

  const {
    handleViewButtonClick,
    groupPolicyInfoFileURL,
    handleBtnBeneficiaryUSBCFileClick,
    setGroupPolicyInfoFileURL,
    showBtnGroupPolicyInfoFile,
    setShowBtnGroupPolicyInfoFile,
    contractAmendmentFileURL,
    setContractAmendmentFileURL,
    showBtnContractAmendment,
    setShowBtnContractAmendment,
    visibleSOB,
    setVisibleSOB,
    sobFileUrl,
    setSobFileUrl,
    openInfo,
    setOpenInfo,
  } = useGroupInformation();

  // FUTURE: May be rename this function to be a common name as its used for G/GA/SOB
  const { getEmployerBenefitSummaryEOC } = useEmployerBenefitSummaryEOC();
  const [visibleUSBC, setVisibleUSBC] = useState(false);
  const [effDateUSBC, setEffDateUSBC] = useState<
    BenefitSummaryUSBCResponseItem[]
  >([]);

  const { setPageLoader } = useGlobalState();

  useEffect(() => {
    document.title = t("pageTitleADA.groupInformation");
    if (dropdownValues.userGroupId) {
      fetchGroupInformation();
    }
  }, [dropdownValues.userGroupId]);

  // Watch for changes in accountId dropdown
  const selectedAccountId = dropdownValues.accountNumber;
  const uniqueAccounts = Array.from(
    new Map(
      user?.groups?.map((item) => [
        `${item.accountId}|${item.accountName}`,
        item,
      ])
    ).values()
  ).sort((a, b) => {
    const byName = customSort(a, b, "accountName");
    if (byName !== 0) return byName;
    return customSort(a, b, "accountId");
  });

  const getFilteredGroups = (accountId: string) => {
    const base = accountId
      ? user?.groups?.filter((group) => group.accountId === accountId)
      : user?.groups;
    return sortGroupsByNameThenId(base || []);
  };

  // Filter groups based on selected account
  const filteredGroups: any = getFilteredGroups(selectedAccountId);

  // Set default account on first load
  useEffect(() => {
    if (
      isAccountNumberFieldFlagEnabled &&
      uniqueAccounts.length > 0 &&
      !selectedAccountId
    ) {
      const firstAccountId = uniqueAccounts[0]?.accountId || "";
      const sortedFiltered = getFilteredGroups(firstAccountId);
      setDropdownValues((prevValues) => ({
        ...prevValues,
        accountNumber: firstAccountId,
        userGroupId: sortedFiltered?.[0]?.groupID ?? prevValues.userGroupId,
      }));
    }
  }, [isAccountNumberFieldFlagEnabled, uniqueAccounts, selectedAccountId]);

  const fetchGroupInformation = async () => {
    setPageLoader("getEmployerGroupInformation", true);
    const request: EmployerGroupInformationRequest = {
      groupID: dropdownValues.userGroupId,
    };
    const masterGroup = user?.groups?.find(
      (group) => group.groupID === dropdownValues.userGroupId
    );
    const masterGroupFallback = masterGroup
      ? { name: masterGroup.groupName, groupID: masterGroup.groupID }
      : null;

    setGroupInformation(null);
    if (dropdownValues.userGroupId?.length) {
      try {
        const response = await getEmployerGroupInformation(request);
        if (response.data) {
          setGroupInformation(response.data);
        } else {
          setGroupInformation(masterGroupFallback);
        }
      } catch (error) {
        console.error("Error fetching employer group information:", error);
        setGroupInformation(masterGroupFallback);
      }
    }
    setPageLoader("getEmployerGroupInformation", false);
  };

  // TODO: Update with actual account numbers from the API or user object data when ready...
  const handleDropdownChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    try {
      if (name === "accountNumber") {
        // When account changes, set the first group from filtered groups as default
        const newFilteredGroups = getFilteredGroups(value);
        setDropdownValues((prevValues) => ({
          ...prevValues,
          accountNumber: value,
          userGroupId: newFilteredGroups?.[0]?.groupID ?? "",
        }));
      } else {
        setDropdownValues((prevValues) => ({
          ...prevValues,
          [name]: value,
        }));
      }
    } catch (error) {
      console.error("Error in handleDropdownChange:", error);
    }
  };

  const setValueUSBC = () => {
    setVisibleUSBC(false);
    setEffDateUSBC([]);
  };
  useEffect(() => {
    const paramsBenefitSummaryUSBC = {
      groupID: groupInformation?.groupID.trim(),
      effDate: formatISODateToISO(groupInformation?.effectiveDate),
    };

    const paramsBenefitSummarySOB = {
      ...paramsBenefitSummaryUSBC,
      url: groupInformation?.summaryOfBenefitsURL,
    };

    if (groupInformation?.groupID) {
      setPageLoader("getEmployerBenefitSummarySOB", true);
      getBenefitSummaryUSBC(paramsBenefitSummaryUSBC).then((response) => {
        if (response.errorCode === "400") {
          setValueUSBC();
        } else if (response?.data?.length) {
          setEffDateUSBC(response?.data);
          setVisibleUSBC(true);
        } else {
          setValueUSBC();
        }
      });
      getEmployerBenefitSummarySOB(paramsBenefitSummarySOB).then((response) => {
        setPageLoader("getEmployerBenefitSummarySOB", false);
        if (response?.data?.fileExists) {
          setVisibleSOB(true);
          setSobFileUrl(response.data.fileURL);
        } else {
          setVisibleSOB(false);
          setSobFileUrl("");
        }
      });

      const beniSummEOCParams: EmployerBenefitSummaryEOCRequest = {
        fileType: EmployerInsuranceGroupFileType.G,
        groupID: groupInformation?.groupID.trim(),
      };
      setPageLoader("getEmployerBenefitSummaryEOC", true);
      getEmployerBenefitSummaryEOC(beniSummEOCParams).then((response) => {
        setPageLoader("getEmployerBenefitSummaryEOC", false);
        if (response?.data?.fileExists) {
          // set file url
          setGroupPolicyInfoFileURL(response.data.fileURL);
          // show insurance group file btn
          setShowBtnGroupPolicyInfoFile(true);
        } else {
          // Do not set file path
          setGroupPolicyInfoFileURL("");
          // Hide insurance group file btn
          setShowBtnGroupPolicyInfoFile(false);
        }
      });

      const contractAmendmentEOCParams: EmployerBenefitSummaryEOCRequest = {
        fileType: EmployerInsuranceGroupFileType.GA,
        groupID: groupInformation?.groupID.trim(),
      };
      setPageLoader("getCcontractAmendmentEOC", true);
      getEmployerBenefitSummaryEOC(contractAmendmentEOCParams).then(
        (response) => {
          setPageLoader("getCcontractAmendmentEOC", false);
          if (response?.data?.fileExists) {
            // set file url
            setContractAmendmentFileURL(response.data.fileURL);
            // show insurance group file btn
            setShowBtnContractAmendment(true);
          } else {
            // Do not set file path
            setContractAmendmentFileURL("");
            // Hide insurance group file btn
            setShowBtnContractAmendment(false);
          }
        }
      );
    }
  }, [groupInformation]);

  return (
    <div id="groupInfoContent">
      <ModalOverlayWithOutClose
        open={openInfo.open}
        callBack={() => setOpenInfo({ open: false })}
        title={openInfo.title}
        content={openInfo.content}
      />
      <Headings
        className="mb-4"
        level={1}
        text={t("groupInformation.header")}
        dataTestId="txtMemberEligibilityPageHeading"
      />
      {/* TODO: Update with actual account numbers from the API or user object data when ready... */}
      {isAccountNumberFieldFlagEnabled &&
        uniqueAccounts &&
        uniqueAccounts.length > 0 && (
          <div className="row mb-4">
            <div className="col-lg-7 col-md-8 col-sm-12 col-12">
              <label
                className="redhat_bold pb-1"
                htmlFor="selectAGroup"
                data-testid="lblGroupNumber"
              >
                {t("groupInformation.selectAccountNumber")}
              </label>
              <div>
                <SelectDropdown
                  name="accountNumber"
                  dataTestId="optSelectAccountNumber"
                  className="form-select"
                  id="selectAccountNumber"
                  value={dropdownValues.accountNumber}
                  onChange={handleDropdownChange}
                >
                  {uniqueAccounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.accountId} {account.accountName}
                    </option>
                  ))}
                </SelectDropdown>
              </div>
            </div>
          </div>
        )}
      {user?.groups && user.groups.length > 1 && (
        <div className="row mb-4">
          <div className="col-lg-7 col-md-8 col-sm-12 col-12">
            <label
              className="redhat_bold pb-1"
              htmlFor="selectAGroup"
              data-testid="lblGroupNumber"
            >
              {t("groupInformation.selectAnotherGroup")}
            </label>
            <div>
              <SelectDropdown
                name="userGroupId"
                dataTestId="optSelectAGroup"
                className="form-select"
                id="selectAGroup"
                value={dropdownValues.userGroupId}
                onChange={handleDropdownChange}
              >
                {(isAccountNumberFieldFlagEnabled
                  ? selectedAccountId && filteredGroups.length > 0
                    ? filteredGroups
                    : []
                  : user?.groups
                )
                  ?.slice()
                  ?.sort(
                    (
                      a: { groupID: string; groupName: string },
                      b: { groupID: string; groupName: string }
                    ) => {
                      const byName = customSort(a, b, "groupName");
                      if (byName !== 0) return byName;
                      return customSort(a, b, "groupID");
                    }
                  )
                  ?.map((item: { groupID: string; groupName: string }) => {
                    return (
                      <option key={`${item.groupID}`} value={item.groupID}>
                        {item.groupID} {item.groupName}
                      </option>
                    );
                  })}
              </SelectDropdown>
            </div>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <Table
          rows={[
            [
              {
                text: (
                  <div className="fw-700" data-testid="employerGroupName">
                    {t("groupInformation.employerGroupName")}:
                  </div>
                ),
              },
              {
                text: (
                  <div data-testid="groupNameValue">
                    {groupInformation?.name}
                  </div>
                ),
              },
            ],
            [
              {
                text: (
                  <div className="fw-700" data-testid="employerGroupNumber">
                    {t("groupInformation.employerGroupNumber")}:
                  </div>
                ),
              },
              {
                text: (
                  <div data-testid="groupIDValue">
                    {groupInformation?.groupID}
                  </div>
                ),
              },
            ],
            [
              {
                text: (
                  <div className="fw-700" data-testid="healthPlanType">
                    {t("groupInformation.healthPlanType")}:
                  </div>
                ),
              },
              {
                text: (
                  <div data-testid="healthPlanTypeValue">
                    {groupInformation?.lOBType}
                  </div>
                ),
              },
            ],
            [
              {
                text: (
                  <div className="fw-700" data-testid="coverageDate">
                    {t("groupInformation.beginningCoverageDate")}:
                  </div>
                ),
              },
              {
                text: (
                  <div data-testid="coverageDateValue">
                    {formatISODateToMDY(groupInformation?.effectiveDate)}
                  </div>
                ),
              },
            ],
            [
              {
                text: (
                  <div className="fw-700" data-testid="renewalDate">
                    {t("groupInformation.renewalDate")}:
                  </div>
                ),
              },
              {
                text: (
                  <div data-testid="renewalDateValue">
                    {formatISODateToMDY(groupInformation?.renewalDate)}
                  </div>
                ),
              },
            ],
            visibleSOB
              ? [
                  {
                    text: (
                      <div className="fw-700" data-testid="benefitSummary">
                        {t("groupInformation.benefitSummary")}:
                      </div>
                    ),
                  },
                  {
                    text: (
                      <PrimaryButton
                        data-testid={"benefitSummaryView"}
                        type="button"
                        className={
                          "primary_btn btn-medium btn-w-134 btn_block_mob"
                        }
                        onClick={() => {
                          if (sobFileUrl) {
                            handleViewButtonClick(
                              EmployerInsuranceGroupFileType.SOB,
                              sobFileUrl
                            );
                          } else {
                            setOpenInfo({
                              open: true,
                              content: t("featureUnavailable"),
                            });
                          }
                        }}
                      >
                        <span className="visually-hidden pe-2">
                          {t("groupInformation.benefitSummary")}
                        </span>
                        {t("enrollmentHistoryForm.view")}
                      </PrimaryButton>
                    ),
                  },
                ]
              : [],
            showBtnGroupPolicyInfoFile
              ? [
                  {
                    text: (
                      <div className="fw-700" data-testid="groupPolicyInfo">
                        {t("groupInformation.groupPolicyInfo")}:
                      </div>
                    ),
                  },
                  {
                    text: (
                      <PrimaryButton
                        data-testid={"benefitSummaryView"}
                        type="button"
                        className={
                          "primary_btn btn-medium btn-w-134 btn_block_mob"
                        }
                        onClick={() => {
                          if (groupPolicyInfoFileURL) {
                            handleViewButtonClick(
                              EmployerInsuranceGroupFileType.G,
                              groupPolicyInfoFileURL
                            );
                          } else {
                            setOpenInfo({
                              open: true,
                              content: t("featureUnavailable"),
                            });
                          }
                        }}
                      >
                        <span className="visually-hidden pe-2">
                          {t("groupInformation.groupPolicyInfo")}
                        </span>
                        {t("enrollmentHistoryForm.view")}
                      </PrimaryButton>
                    ),
                  },
                ]
              : [],
            visibleUSBC
              ? [
                  {
                    text: (
                      <div
                        className="fw-700"
                        data-testid="summaryOfBenefitsAndCoverage"
                      >
                        {t(
                          "groupInformation.uniformSummaryOfBenefitsAndCoverage"
                        )}
                        :
                      </div>
                    ),
                  },
                  {
                    text: (
                      <div data-testid="benefitsAndCoverageValue">
                        <div className="d-flex flex-column gap-2">
                          {effDateUSBC.map((item: any, index) => (
                            <div key={index - item.groupID}>
                              <Link
                                to=""
                                onClick={() =>
                                  handleBtnBeneficiaryUSBCFileClick(item)
                                }
                              >
                                <span className="visually-hidden pe-2">
                                  {t(
                                    "groupInformation.uniformSummaryOfBenefitsAndCoverage"
                                  )}
                                </span>
                                {t("enrollmentHistoryForm.effective")}
                                <span className="visually-hidden ps-2">
                                  {t("groupInformation.date")}
                                </span>
                                : {formatISODateToMDY(item.effDate)}
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  },
                ]
              : [],
            showBtnContractAmendment
              ? [
                  {
                    text: (
                      <div className="fw-700" data-testid="contractAmendment">
                        {t("groupInformation.contractAmendment")}:
                      </div>
                    ),
                  },
                  {
                    text: (
                      <PrimaryButton
                        data-testid={"benefitSummaryView"}
                        type="button"
                        className={
                          "primary_btn btn-medium btn-w-134 btn_block_mob"
                        }
                        onClick={() => {
                          if (contractAmendmentFileURL) {
                            handleViewButtonClick(
                              EmployerInsuranceGroupFileType.GA,
                              contractAmendmentFileURL
                            );
                          } else {
                            setOpenInfo({
                              open: true,
                              content: t("featureUnavailable"),
                            });
                          }
                        }}
                      >
                        <span className="visually-hidden pe-2">
                          {t("groupInformation.contractAmendment")}
                        </span>
                        {t("enrollmentHistoryForm.view")}
                      </PrimaryButton>
                    ),
                  },
                ]
              : [],
          ]}
        />
      </div>
    </div>
  );
};

export default GroupInformation;
